import React from "react";
import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Close } from "@mui/icons-material";
import SelectSearchField from "./SelectSearchField";
import {
  DROPDOWN_MAX_CONTENT_WIDTH_PX,
  SELECT_COLOR,
  getDropdownAnchorUnderlineSx,
} from "./selectConstants";

const DEFAULT_VALUE_SX = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  alignItems: "center",
  py: 0.5,
};

/**
 * Shared visual frame for custom selectors.
 *
 * Option rendering and selection behavior are passed as children so single,
 * multi, grouped and tree selectors retain their existing contracts.
 */
export default function SelectFieldShell({
  dropdown,
  elemId,
  label,
  shrink,
  disabled = false,
  enableFullWidth = false,
  canClear = false,
  onClear,
  valueDisplay,
  endAdornment,
  fieldError,
  errorContent,
  showSearch = true,
  onAnchorClick,
  onAnchorFocus,
  onAnchorKeyDown,
  onListKeyDown,
  children,
  contentWidth,
  popperStyle,
  disablePortal,
  anchorPaddingTop = 2,
  underlineActive,
  underlineTransition = "none",
  valueContainerSx,
  compactSearch = false,
  preserveListFocus = false,
}) {
  const {
    open,
    close,
    searchTerm,
    setSearchTerm,
    isFocused,
    setIsFocused,
    actualPlacement,
    dropdownMaxHeight,
    anchorWidth,
    availableWidth,
    availableWidthEnd,
    anchorIsFixed,
    anchorRef,
    paperRef,
    searchInputRef,
    popperRef,
  } = dropdown;

  const maxWidthStyle = enableFullWidth
    ? { maxWidth: "100%" }
    : { maxWidth: "220px", width: "100%" };
  const underlineIsActive = underlineActive ?? open;

  /**
   * Panel width rule shared by every selector:
   * - never narrower than the field,
   * - may grow past the field to fit the longest option (contentWidth),
   * - capped by the shared content cap and by the viewport on both sides.
   * When the right side is too tight, the extra width extends left.
   * The field itself does not change width, and the overlay stays inside the
   * viewport so opening the list does not add a horizontal page scrollbar.
   * Long labels still wrap inside whatever width this resolves to.
   */
  const requestedContentWidth = Math.min(
    contentWidth ?? 0,
    DROPDOWN_MAX_CONTENT_WIDTH_PX
  );
  const desiredWidth = Math.max(anchorWidth, requestedContentWidth);
  const spaceOnRight = availableWidth;
  const spaceOnLeft = availableWidthEnd;
  const measured = spaceOnRight > 0 && spaceOnLeft > 0;
  const fitsOnRight = measured && desiredWidth <= spaceOnRight;
  const alignEnd = measured && fitsOnRight !== true && spaceOnLeft > spaceOnRight;
  const spaceForAlignment = alignEnd ? spaceOnLeft : spaceOnRight;
  const resolvedPopperWidth = measured
    ? Math.min(desiredWidth, Math.max(anchorWidth, spaceForAlignment))
    : anchorWidth;
  const verticalPlacement = actualPlacement.startsWith("top") ? "top" : "bottom";
  const resolvedPlacement = `${verticalPlacement}-${alignEnd ? "end" : "start"}`;

  const resolvedPopperStyle = {
    minWidth: anchorWidth > 0 ? anchorWidth : undefined,
    width: resolvedPopperWidth > 0 ? resolvedPopperWidth : undefined,
    maxWidth: resolvedPopperWidth > 0 ? resolvedPopperWidth : undefined,
    // Callers keep the final say for one-off cases.
    ...popperStyle,
  };

  return (
    <ClickAwayListener onClickAway={() => open && close()}>
      <Box sx={{ width: "100%", position: "relative", ...maxWidthStyle }}>
        <Box
          id={elemId}
          ref={anchorRef}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          tabIndex={disabled ? -1 : 0}
          onClick={onAnchorClick}
          onFocus={() => {
            if (disabled) return;
            setIsFocused(true);
            onAnchorFocus?.();
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={onAnchorKeyDown}
          sx={{
            position: "relative",
            cursor: disabled ? "default" : "pointer",
            outline: "none",
            pt: anchorPaddingTop,
            opacity: disabled ? 0.6 : 1,
            ...getDropdownAnchorUnderlineSx({
              underlineIsActive,
              underlineTransition,
              disabled,
            }),
            "& .cap-clear-btn": { opacity: 0 },
            "&:hover .cap-clear-btn": { opacity: 1 },
            "& .cap-clear-btn-visible": { opacity: 1 },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: shrink ? 0 : 22,
              fontSize: shrink ? 12 : 14,
              color: SELECT_COLOR,
              pointerEvents: "none",
              transformOrigin: "top left",
              transition: "all 160ms ease",
            }}
          >
            {label}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 32 }}>
            <Box sx={{ ...DEFAULT_VALUE_SX, ...valueContainerSx }}>{valueDisplay}</Box>

            <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 0.4 }}>
              {canClear && !disabled && (
                <Close
                  className={`cap-clear-btn${open || isFocused ? " cap-clear-btn-visible" : ""}`}
                  onClick={onClear}
                  sx={{ cursor: "pointer", fontSize: 18 }}
                />
              )}
              {endAdornment}
              {open ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </Box>
          </Box>
        </Box>

        {errorContent ??
          (fieldError ? (
            <Typography
              variant="caption"
              sx={{ color: SELECT_COLOR, mt: 0.5, display: "block" }}
            >
              {fieldError}
            </Typography>
          ) : null)}

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement={resolvedPlacement}
          popperRef={popperRef}
          disablePortal={disablePortal}
          popperOptions={{ strategy: anchorIsFixed ? "fixed" : "absolute" }}
          sx={{ zIndex: 1400 }}
          modifiers={[
            {
              name: "eventListeners",
              options: { scroll: false, resize: false },
            },
            {
              // Placement and height are maintained by useSelectDropdown.
              name: "preventOverflow",
              enabled: false,
            },
            {
              name: "flip",
              enabled: false,
            },
          ]}
          style={resolvedPopperStyle}
        >
          <Paper
            ref={paperRef}
            role="listbox"
            tabIndex={-1}
            sx={{
              maxHeight: dropdownMaxHeight,
              overflowY: "auto",
              overflowX: "hidden",
              outline: "none",
            }}
            onMouseDown={(event) => {
              if (preserveListFocus && event.target.tagName !== "INPUT") {
                event.preventDefault();
              }
              event.stopPropagation();
            }}
            onKeyDown={onListKeyDown}
          >
            {showSearch && (
              <SelectSearchField
                inputRef={searchInputRef}
                id={`${elemId ?? "selector"}_search`}
                value={searchTerm}
                onChange={setSearchTerm}
                onKeyDown={onListKeyDown}
                compact={compactSearch}
              />
            )}
            {children}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
